/**
 * API Administrator Interface
 * 
 * Queue-based interface for managing API quote requests, field approvals, and pricing
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import {
    Box,
    Paper,
    Typography,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Divider,
    Badge,
    IconButton,
    Tooltip,
    Tab,
    Tabs,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Settings as SettingsIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    ArrowBack as ArrowBackIcon,
    Queue as QueueIcon,
    Key as KeyIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
import type { 
    ApiQuoteRequest, 
    ApiQuoteFieldValue, 
    ApiFieldDefinition,
    QuoteStatus 
} from '../models/apiTypes';
import { 
    mockApiQuoteRepository,
    mockApiFieldRepository, 
    mockApiUserRepository, 
    mockApiPricingIntegrationService 
} from '../services/apiClientService';
import { ApiFieldMappingInterface } from './ApiFieldMappingInterface';
import { ApiKeyManagementInterface } from './ApiKeyManagementInterface';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-tabpanel-${index}`}
            aria-labelledby={`admin-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// Helper function to safely format dates
const formatDate = (dateInput: string | Date | undefined): string => {
    try {
        if (!dateInput) {
            return 'No Date';
        }
        const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return date.toLocaleDateString();
    } catch (error) {
        console.error('Date formatting error:', error, 'Input:', dateInput);
        return 'Invalid Date';
    }
};

// Helper function to sort quotes
const sortQuotes = (quotes: ApiQuoteRequest[], sortBy: 'date' | 'status', sortOrder: 'asc' | 'desc'): ApiQuoteRequest[] => {
    const sorted = [...quotes].sort((a, b) => {
        if (sortBy === 'status') {
            // Define status order for progression-based sorting
            const statusOrder = [
                'Received',
                'Quoting in Progress',
                'Quoted',
                'Accepted',
                'Awaiting Design',
                'Awaiting Production',
                'In Production - Layout',
                'In Production - 1st Assembly',
                'In Production - 2nd Assembly',
                'In Production - Handwork',
                'Quality Assurance',
                'Checkout',
                'Shipped',
                'Deleted'
            ];
            
            const aStatusIndex = statusOrder.indexOf(a.status);
            const bStatusIndex = statusOrder.indexOf(b.status);
            
            // Primary sort by status
            if (aStatusIndex !== bStatusIndex) {
                return sortOrder === 'asc' ? aStatusIndex - bStatusIndex : bStatusIndex - aStatusIndex;
            }
            
            // Secondary sort by date (newest first for same status)
            const aDate = new Date(a.submittedAt || a.createdAt || 0);
            const bDate = new Date(b.submittedAt || b.createdAt || 0);
            return bDate.getTime() - aDate.getTime();
        } else {
            // Sort by date
            const aDate = new Date(a.submittedAt || a.createdAt || 0);
            const bDate = new Date(b.submittedAt || b.createdAt || 0);
            
            if (sortOrder === 'asc') {
                return aDate.getTime() - bDate.getTime();
            } else {
                return bDate.getTime() - aDate.getTime(); // newest first
            }
        }
    });
    
    return sorted;
};

export const ApiAdminInterface: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentTab, setCurrentTab] = useState(0);
    const [quotes, setQuotes] = useState<ApiQuoteRequest[]>([]);
    const [selectedQuote, setSelectedQuote] = useState<ApiQuoteRequest | null>(null);
    const [quoteFieldValues, setQuoteFieldValues] = useState<ApiQuoteFieldValue[]>([]);
    const [fieldDefinitions, setFieldDefinitions] = useState<ApiFieldDefinition[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'All'>('All');
    const [sortBy, setSortBy] = useState<'date' | 'status'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // desc = newest first
    
    // Dialog states
    const [editingField, setEditingField] = useState<ApiFieldDefinition | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {}
    });

    // Statistics
    const [stats, setStats] = useState({
        totalQuotes: 0,
        pendingApproval: 0,
        duplicateQuotes: 0,
        unknownFields: 0
    });

    useEffect(() => {
        loadData();
    }, [statusFilter, sortBy, sortOrder]);

    /**
     * Handle deleting a quote from the queue (removes from display, keeps in database)
     */
    const handleDeleteQuote = async (quoteId: string) => {
        try {
            console.log(`🗑️ Attempting to soft delete quote ${quoteId}`);
            console.log(`📊 Before delete: ${quotes.length} quotes`);
            
            // Get Firebase token for authentication
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            
            if (!token) {
                console.error('No authentication token available for delete');
                return;
            }

            // Make direct API call to soft delete (changes status to 'Deleted')
            const response = await fetch(`/api/v1/admin/quotes/${quoteId}/delete`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete quote');
            }

            const result = await response.json();
            console.log(`✅ Quote soft deleted successfully:`, result);
            
            // Refresh data to reflect the change
            await loadData();
            
            // If this was the selected quote, clear the selection
            if (selectedQuote?.id === quoteId) {
                setSelectedQuote(null);
                console.log(`🎯 Cleared selected quote`);
            }

            console.log(`✅ Successfully soft deleted quote ${quoteId} (status changed to 'Deleted')`);
        } catch (error) {
            console.error('Failed to soft delete quote:', error);
            // Show error to user
            alert(`Failed to delete quote: ${error instanceof Error ? error.message : 'Unknown error'}`);
            // If there was an error, reload the data to restore the correct state
            loadData();
        }
    };

    /**
     * Load all data
     */
    const loadData = async () => {
        setLoading(true);
        try {
            // Get Firebase token for authentication
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            
            if (!token) {
                console.error('No authentication token available');
                return;
            }

            // Fetch quotes from the real API admin endpoint
            const response = await fetch('/api/v1/admin/quotes', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            let quotesData: ApiQuoteRequest[] = [];

            if (response.ok) {
                const data = await response.json();
                quotesData = data.data || [];
                console.log(`📊 Loaded ${quotesData.length} quotes from API:`, quotesData.map(q => ({ id: q.id, status: q.status, isDuplicate: q.isDuplicate })));
            } else {
                console.warn('API request failed, using mock data fallback');
                // Fallback to mock data if API fails
                if (statusFilter === 'All') {
                    quotesData = await mockApiQuoteRepository.findAll();
                } else {
                    quotesData = await mockApiQuoteRepository.findAll(statusFilter);
                }
            }

            console.log(`🔍 Before filtering: ${quotesData.length} quotes, statusFilter: ${statusFilter}`);

            // Apply filters
            if (statusFilter !== 'All') {
                const beforeFilter = quotesData.length;
                quotesData = quotesData.filter(q => q.status === statusFilter);
                console.log(`🔍 Status filter applied: ${beforeFilter} -> ${quotesData.length} quotes`);
            }


            // Apply sorting
            quotesData = sortQuotes(quotesData, sortBy, sortOrder);
            console.log(`🔄 Quotes sorted by ${sortBy} (${sortOrder})`);

            console.log(`✅ Final quotes to display: ${quotesData.length}`);
            setQuotes(quotesData);

            // Load field definitions from the real API
            const fieldResponse = await fetch('/api/v1/admin/field-definitions', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (fieldResponse.ok) {
                const fieldData = await fieldResponse.json();
                setFieldDefinitions(fieldData.data || []);
                console.log(`📊 Loaded ${fieldData.data?.length || 0} field definitions from API`);
            } else {
                // Fallback to mock data
                const definitions = await mockApiFieldRepository.findAllDefinitions();
                setFieldDefinitions(definitions);
            }

            // Load statistics
            const quoteStats = await mockApiQuoteRepository.getQuoteStats();
            const fieldStats = await mockApiFieldRepository.getFieldStats();
            const userStats = await mockApiUserRepository.getUserStats();
            
            setStats({
                totalQuotes: quoteStats.totalQuotes,
                pendingApproval: quoteStats.quotesNeedingApproval,
                duplicateQuotes: quoteStats.duplicateQuotes,
                unknownFields: fieldStats.unknownFields
            });

        } catch (error) {
            console.error('Failed to load admin data:', error);
        }
        setLoading(false);
    };

    /**
     * Select a quote and load its field values
     */
    const selectQuote = async (quote: ApiQuoteRequest) => {
        setSelectedQuote(quote);
        try {
            // Convert quote properties to field values for display
            const excludedFields = ['id', 'userId', 'userEmail', 'userName', 'userCompany', 'submittedAt', 'updatedAt', 'status', 'msrpPrice', 'dealerPrice', 'manufacturingPrice', 'productNumber', 'requestId'];
            const fieldValues = Object.entries(quote)
                .filter(([key]) => !excludedFields.includes(key))
                .map(([key, value]) => ({
                    id: `${quote.id}-${key}`,
                    fieldName: key,
                    value: value?.toString() || '',
                    status: 'approved' as const,
                    quoteId: quote.id
                }));
            
            setQuoteFieldValues(fieldValues);
            console.log(`📋 Loaded ${fieldValues.length} field values for quote ${quote.id}`);
        } catch (error) {
            console.error('Failed to load quote field values:', error);
            setQuoteFieldValues([]);
        }
    };

    /**
     * Update quote status
     */
    const updateQuoteStatus = async (quote: ApiQuoteRequest, newStatus: QuoteStatus) => {
        try {
            // Get Firebase token for authentication
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            
            if (!token) {
                console.error('No authentication token available for status update');
                return;
            }

            console.log(`🔄 Updating quote ${quote.id} status from '${quote.status}' to '${newStatus}'`);

            // Make direct API call to change status
            const response = await fetch(`/api/v1/admin/quotes/${quote.id}/status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update quote status');
            }

            const result = await response.json();
            console.log(`✅ Quote status updated successfully:`, result);
            
            // Set timestamps for specific statuses if needed
            if (newStatus === 'Quoted' || newStatus === 'Shipped') {
                const updates: Partial<ApiQuoteRequest> = {};
                if (newStatus === 'Quoted') {
                    updates.quotedAt = new Date();
                } else if (newStatus === 'Shipped') {
                    updates.shippedAt = new Date();
                }
                
                // Update timestamps via API
                const updateResponse = await fetch(`/api/v1/admin/quotes/${quote.id}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(updates)
                });
                
                if (updateResponse.ok) {
                    console.log(`✅ Quote timestamps updated`);
                }
            }
            
            // Update the selected quote immediately with the new status
            if (selectedQuote?.id === quote.id) {
                setSelectedQuote({
                    ...selectedQuote,
                    status: newStatus,
                    updatedAt: new Date().toISOString()
                });
            }
            
            // Refresh data to get latest state from server
            await loadData();
            
        } catch (error) {
            console.error('Failed to update quote status:', error);
            // Show error to user (could add a toast notification here)
            alert(`Failed to update quote status: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    /**
     * Update quote field (like prices)
     */
    const updateQuoteField = async (fieldName: string, value: string) => {
        if (!selectedQuote) return;
        
        try {
            // Update the local state immediately for responsive UI
            setSelectedQuote({
                ...selectedQuote,
                [fieldName]: value,
                updatedAt: new Date().toISOString()
            });

            // Get Firebase token for authentication
            const { authService } = await import('../services/authService');
            const token = await authService.getIdToken();
            
            if (!token) {
                console.error('No authentication token available for field update');
                return;
            }

            // Update via API (debounced - could be improved with useDebounce hook)
            const response = await fetch(`/api/v1/admin/quotes/${selectedQuote.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ [fieldName]: value })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update quote field');
            }

            console.log(`✅ Updated quote ${selectedQuote.id} field '${fieldName}' to '${value}'`);
            
        } catch (error) {
            console.error('Failed to update quote field:', error);
            // Could revert the local state change here if needed
        }
    };

    /**
     * Process quote pricing
     */
    const processQuotePricing = async (quote: ApiQuoteRequest) => {
        try {
            setLoading(true);
            await mockApiPricingIntegrationService.processQuotePricing(quote.id);
            await loadData();
            
            // Update selected quote
            if (selectedQuote?.id === quote.id) {
                const updated = quotes.find(q => q.id === quote.id);
                if (updated) setSelectedQuote(updated);
            }
            
        } catch (error) {
            console.error('Failed to process quote pricing:', error);
        }
        setLoading(false);
    };

    /**
     * Approve field value
     */
    const approveFieldValue = async (fieldValue: ApiQuoteFieldValue) => {
        try {
            await mockApiFieldRepository.updateFieldValue(fieldValue.id, {
                needsApproval: false
            });
            
            // Refresh field values
            if (selectedQuote) {
                const updatedValues = await mockApiFieldRepository.findFieldValues(selectedQuote.id);
                setQuoteFieldValues(updatedValues);
            }
            
        } catch (error) {
            console.error('Failed to approve field value:', error);
        }
    };

    /**
     * Create field definition from unknown field
     */
    const createFieldDefinition = async (fieldValue: ApiQuoteFieldValue) => {
        try {
            const newDefinition: Omit<ApiFieldDefinition, 'id' | 'createdAt' | 'updatedAt'> = {
                alternativeFieldName: fieldValue.fieldName,
                apiFieldName: fieldValue.fieldName,
                exampleValue: String(fieldValue.value),
                publish: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'variable',
                valueType: 'text',
                isApproved: true
            };

            await mockApiFieldRepository.createDefinition(newDefinition);
            
            // Mark field value as known
            await mockApiFieldRepository.updateFieldValue(fieldValue.id, {
                isUnknownField: false,
                needsApproval: false
            });

            await loadData();
            
        } catch (error) {
            console.error('Failed to create field definition:', error);
        }
    };

    /**
     * Get status color
     */
    const getStatusColor = (status: QuoteStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (status) {
            case 'Received': return 'info';
            case 'Quoting in Progress': return 'warning';
            case 'Quoted': return 'primary';
            case 'Accepted': return 'success';
            case 'Shipped': return 'default';
            default: return 'secondary';
        }
    };

    /**
     * Render quote list
     */
    const renderQuoteList = () => (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Filter by Status"
                        onChange={(e) => setStatusFilter(e.target.value as QuoteStatus | 'All')}
                    >
                        <MenuItem value="All">All Statuses</MenuItem>
                        <MenuItem value="Received">Received</MenuItem>
                        <MenuItem value="Quoting in Progress">Quoting in Progress</MenuItem>
                        <MenuItem value="Quoted">Quoted</MenuItem>
                        <MenuItem value="Accepted">Accepted</MenuItem>
                        <MenuItem value="Awaiting Design">Awaiting Design</MenuItem>
                        <MenuItem value="Awaiting Production">Awaiting Production</MenuItem>
                        <MenuItem value="In Production - Layout">In Production - Layout</MenuItem>
                        <MenuItem value="In Production - 1st Assembly">In Production - 1st Assembly</MenuItem>
                        <MenuItem value="In Production - 2nd Assembly">In Production - 2nd Assembly</MenuItem>
                        <MenuItem value="In Production - Handwork">In Production - Handwork</MenuItem>
                        <MenuItem value="Quality Assurance">Quality Assurance</MenuItem>
                        <MenuItem value="Checkout">Checkout</MenuItem>
                        <MenuItem value="Shipped">Shipped</MenuItem>
                    </Select>
                </FormControl>
                
                <IconButton onClick={loadData} disabled={loading}>
                    <RefreshIcon />
                </IconButton>
            </Box>
            
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                        value={sortBy}
                        label="Sort By"
                        onChange={(e) => setSortBy(e.target.value as 'date' | 'status')}
                    >
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="status">Status</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Order</InputLabel>
                    <Select
                        value={sortOrder}
                        label="Order"
                        onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                    >
                        <MenuItem value="desc">
                            {sortBy === 'date' ? 'Newest First' : 'Latest Stage First'}
                        </MenuItem>
                        <MenuItem value="asc">
                            {sortBy === 'date' ? 'Oldest First' : 'Earliest Stage First'}
                        </MenuItem>
                    </Select>
                </FormControl>
            </Box>
            
            {sortBy === 'status' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 1 }}>
                    Status sort: Primary by manufacturing stage, secondary by date (newest first within each stage)
                </Typography>
            )}

            <List>
                {quotes.map((quote) => (
                    <ListItem key={quote.id} disablePadding>
                        <ListItemButton
                            selected={selectedQuote?.id === quote.id}
                            onClick={() => selectQuote(quote)}
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle2">
                                            {quote.productNumber}
                                        </Typography>
                                        {quote.isDuplicate && (
                                            <Badge color="warning" variant="dot">
                                                <WarningIcon fontSize="small" color="warning" />
                                            </Badge>
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {quote.userEmail} • {formatDate(quote.submittedAt)}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={quote.status}
                                            color={getStatusColor(quote.status)}
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Box>
                                }
                            />
                        </ListItemButton>
                        <IconButton 
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteQuote(quote.id);
                            }}
                            color="error"
                            size="small"
                            sx={{ mr: 1 }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    /**
     * Render quote details
     */
    const renderQuoteDetails = () => {
        if (!selectedQuote) {
            return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        Select a quote from the list to view details
                    </Typography>
                </Box>
            );
        }

        return (
            <Box>
                {/* Quote Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {selectedQuote.productNumber}
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Customer Email"
                                value={selectedQuote.userEmail || ''}
                                disabled
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={selectedQuote.status}
                                    label="Status"
                                    onChange={(e) => updateQuoteStatus(selectedQuote, e.target.value as QuoteStatus)}
                                >
                                    <MenuItem value="Received">Received</MenuItem>
                                    <MenuItem value="Quoting in Progress">Quoting in Progress</MenuItem>
                                    <MenuItem value="Quoted">Quoted</MenuItem>
                                    <MenuItem value="Accepted">Accepted</MenuItem>
                                    <MenuItem value="Awaiting Design">Awaiting Design</MenuItem>
                                    <MenuItem value="Awaiting Production">Awaiting Production</MenuItem>
                                    <MenuItem value="In Production - Layout">In Production - Layout</MenuItem>
                                    <MenuItem value="In Production - 1st Assembly">In Production - 1st Assembly</MenuItem>
                                    <MenuItem value="In Production - 2nd Assembly">In Production - 2nd Assembly</MenuItem>
                                    <MenuItem value="In Production - Handwork">In Production - Handwork</MenuItem>
                                    <MenuItem value="Quality Assurance">Quality Assurance</MenuItem>
                                    <MenuItem value="Checkout">Checkout</MenuItem>
                                    <MenuItem value="Shipped">Shipped</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => processQuotePricing(selectedQuote)}
                            disabled={loading}
                        >
                            Calculate Pricing
                        </Button>
                        
                        {selectedQuote.isDuplicate && (
                            <Chip
                                icon={<WarningIcon />}
                                label="Duplicate"
                                color="warning"
                            />
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Pricing Information */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Pricing Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="MSRP Price"
                                value={selectedQuote.msrpPrice || ''}
                                type="number"
                                InputProps={{ startAdornment: '$' }}
                                size="small"
                                onChange={(e) => updateQuoteField('msrpPrice', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Dealer Price"
                                value={selectedQuote.dealerPrice || ''}
                                type="number"
                                InputProps={{ startAdornment: '$' }}
                                size="small"
                                onChange={(e) => updateQuoteField('dealerPrice', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Manufacturing Price"
                                value={selectedQuote.manufacturingPrice || ''}
                                type="number"
                                InputProps={{ startAdornment: '$' }}
                                size="small"
                                onChange={(e) => updateQuoteField('manufacturingPrice', e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Field Values */}
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Field Values
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Field Name</TableCell>
                                    <TableCell>Value</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quoteFieldValues.map((fieldValue) => (
                                    <TableRow key={fieldValue.id}>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {fieldValue.fieldName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {String(fieldValue.value)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {fieldValue.isUnknownField && (
                                                <Chip
                                                    size="small"
                                                    label="Unknown Field"
                                                    color="error"
                                                    icon={<WarningIcon />}
                                                />
                                            )}
                                            {fieldValue.needsApproval && (
                                                <Chip
                                                    size="small"
                                                    label="Needs Approval"
                                                    color="warning"
                                                    icon={<WarningIcon />}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {fieldValue.needsApproval && (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => approveFieldValue(fieldValue)}
                                                    >
                                                        <ApproveIcon />
                                                    </IconButton>
                                                    {fieldValue.isUnknownField && (
                                                        <Tooltip title="Create Field Definition">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => createFieldDefinition(fieldValue)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        );
    };

    /**
     * Render quote status funnel chart
     */
    const renderQuoteStatusChart = () => {
        // Calculate status counts from current quotes (exclude deleted)
        const activeQuotes = quotes.filter(q => q.status !== 'Deleted');
        const statusCounts = activeQuotes.reduce((acc, quote) => {
            const status = quote.status;
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        // Define the funnel order (typical sales funnel progression)
        const funnelStatuses = [
            'Received',
            'Quoting in Progress', 
            'Quoted',
            'Accepted',
            'Awaiting Design',
            'Awaiting Production',
            'In Production - Layout',
            'In Production - 1st Assembly',
            'In Production - 2nd Assembly',
            'In Production - Handwork',
            'Quality Assurance',
            'Checkout',
            'Shipped'
        ];

        // Get maximum count for scaling bars
        const maxCount = Math.max(...Object.values(statusCounts), 1);

        // Filter to only show statuses that have quotes
        const chartData = funnelStatuses
            .map(status => ({
                status,
                count: statusCounts[status] || 0,
                percentage: maxCount > 0 ? (statusCounts[status] || 0) / maxCount * 100 : 0
            }))
            .filter(item => item.count > 0); // Only show statuses with quotes

        if (chartData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        No quotes available for chart display
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ width: '100%' }}>
                {chartData.map((item, index) => (
                    <Box key={item.status} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        {/* Status Label */}
                        <Box sx={{ width: '200px', mr: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {item.status}
                            </Typography>
                        </Box>
                        
                        {/* Bar */}
                        <Box sx={{ flex: 1, position: 'relative' }}>
                            <Box
                                sx={{
                                    height: '24px',
                                    backgroundColor: `hsl(${210 + index * 15}, 70%, ${60 + index * 2}%)`,
                                    borderRadius: '4px',
                                    width: `${item.percentage}%`,
                                    minWidth: '40px',
                                    transition: 'width 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    pr: 1
                                }}
                            >
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                                    }}
                                >
                                    {item.count}
                                </Typography>
                            </Box>
                        </Box>
                        
                        {/* Percentage */}
                        <Box sx={{ width: '60px', ml: 2, textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                                {((item.count / activeQuotes.length) * 100).toFixed(1)}%
                            </Typography>
                        </Box>
                    </Box>
                ))}
                
                {/* Chart Footer */}
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                        Total Active Quotes: {activeQuotes.length} • Excludes 'Deleted' status
                    </Typography>
                </Box>
            </Box>
        );
    };

    /**
     * Render statistics dashboard
     */
    const renderStatistics = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                API Statistics
            </Typography>
            
            <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                            {stats.totalQuotes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Quotes
                        </Typography>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">
                            {stats.pendingApproval}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pending Approval
                        </Typography>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main">
                            {stats.duplicateQuotes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Duplicate Quotes
                        </Typography>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">
                            {stats.unknownFields}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Unknown Fields
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Quote Status Funnel Chart */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Quote Status Funnel
                </Typography>
                <Paper sx={{ p: 3 }}>
                    {renderQuoteStatusChart()}
                </Paper>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ width: '100%', minHeight: 'calc(100vh - 160px)' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton 
                        onClick={() => navigate('/')} 
                        sx={{ mr: 2 }}
                        color="primary"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5">
                        API Administrator Interface
                    </Typography>
                </Box>
                
                <Tabs value={currentTab} onChange={(_, value) => setCurrentTab(value)}>
                    <Tab label="Quote Queue" icon={<QueueIcon />} />
                    {authService.canAccessFieldMapping() && (
                        <Tab label="Field Mapping" icon={<SettingsIcon />} />
                    )}
                    {authService.canAccessApiKeys() && (
                        <Tab label="API Keys" icon={<KeyIcon />} />
                    )}
                    <Tab label="Statistics" icon={<AnalyticsIcon />} />
                </Tabs>
            </Box>

            {/* Content */}
            <Box sx={{ overflow: 'auto' }}>
                {/* Quote Queue Tab - Always index 0 */}
                <TabPanel value={currentTab} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2, maxHeight: '500px', overflow: 'auto' }}>
                                <Typography variant="h6" gutterBottom>
                                    Quote Queue ({quotes.length})
                                </Typography>
                                {renderQuoteList()}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 2, maxHeight: '500px', overflow: 'auto' }}>
                                {renderQuoteDetails()}
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Field Mapping Tab - Only for superadmin */}
                {authService.canAccessFieldMapping() && (
                    <TabPanel value={currentTab} index={1}>
                        <ApiFieldMappingInterface />
                    </TabPanel>
                )}

                {/* API Keys Tab - For superadmin and api_customer */}
                {authService.canAccessApiKeys() && (
                    <TabPanel value={currentTab} index={authService.canAccessFieldMapping() ? 2 : 1}>
                        <ApiKeyManagementInterface />
                    </TabPanel>
                )}

                {/* Statistics Tab - Always available, dynamic index */}
                <TabPanel value={currentTab} index={
                    authService.canAccessFieldMapping() && authService.canAccessApiKeys() ? 3 :
                    (authService.canAccessFieldMapping() || authService.canAccessApiKeys()) ? 2 : 1
                }>
                    {renderStatistics()}
                </TabPanel>
            </Box>
        </Box>
    );
};

export default ApiAdminInterface;